import axios, {AxiosError, AxiosResponse} from "axios";
import {EnvironmentConfig} from "../common/configs/environment.config";
import {getAllIndexedData, Stores} from "../common/index.db";
import {AuthToken} from "../common/types/authToken.type";
import {AxiosRequestConfig} from "axios/index";
import {BlobResponse} from "../common/interfaces/blobResponse";
import {tokenEventEmitter} from "../common/utils/eventEmitter.util";

export const axiosRequest = async <T>(
    method: "GET" | "POST" | "DELETE" | "PUT",
    url: string,
    body?: any,
    isAuthEndpoint?: boolean,
    options?: Omit<AxiosRequestConfig, "url" | "method" | "data" | "headers">
) => {
    let baseUrl = `${EnvironmentConfig.BaseUrl}/`;
    const tokens = await getAllIndexedData<AuthToken>(Stores.Auth);

    if (isAuthEndpoint == undefined || !isAuthEndpoint) {
        baseUrl += 'api/'
    }

    let config: any = {
        url: `${baseUrl}${url}`,
        method,
        data: body,
    }

    let headerConfigs : {} = {
        "Application-Name": "thatsmebaby"
    }
    
    if (Array.isArray(tokens) && tokens.length) {
        headerConfigs = {
            "Authorization": `Bearer ${tokens[0].access_token}`,
            ...headerConfigs
        }
    }

    if (isAuthEndpoint) {
        headerConfigs = {
            "Content-Type": "application/x-www-form-urlencoded",
            ...headerConfigs
        }
    }

    config = {
        headers: headerConfigs,
        ...config,
        ...options
    }

    try {
        const response = await axios.request<T>(config);
        return response.data;
    } catch (e) {
        const error = e as AxiosError;
        if (error.response && error.response.status === 401) {
            // Access token expired, attempt to refresh it
            const newTokens = await refreshAccessToken();
            if (newTokens) {
                // Retry the original request with the new access token
                const retryConfig = {
                    ...config,
                    headers: {
                        ...headerConfigs,
                        "Authorization": `Bearer ${newTokens.access_token}`,
                    },
                };
                const retryResponse = await axios.request<T>(retryConfig);
                return retryResponse.data;
            }
        }
        throw error; // If refresh failed or error is not 401, propagate the error
    }
};

export const axiosBlobRequest = async (
    method: "GET" | "POST",
    url: string,
    body?: any,
    options?: Omit<AxiosRequestConfig, "url" | "method" | "data" | "headers">
): Promise<BlobResponse> => {
    let baseUrl = `${EnvironmentConfig.BaseUrl}/api/`;
    const tokens = await getAllIndexedData<AuthToken>(Stores.Auth);

    let config: any = {
        url: `${baseUrl}${url}`,
        method,
        data: body
    }

    let headerConfigs : {} = {
        "Application-Name": "thatsmebaby"
    }
    if (Array.isArray(tokens) && tokens.length) {
        headerConfigs = {
            "Authorization": `Bearer ${tokens[0].access_token}`,
            ...headerConfigs
        }
    }

    config = {
        headers: headerConfigs,
        ...config,
        ...options
    }

    try {
        const response = await axios.request(config);

        return processBlobResponse(response);

    } catch (e) {
        const error = e as AxiosError;
        if (error.response && error.response.status === 401) {
            // Access token expired, attempt to refresh it
            const newTokens = await refreshAccessToken();
            if (newTokens) {
                // Retry the original request with the new access token
                const retryConfig = {
                    ...config,
                    headers: {
                        ...headerConfigs,
                        "Authorization": `Bearer ${newTokens.access_token}`,
                    },
                };
                const retryResponse = await axios.request(retryConfig);

                return processBlobResponse(retryResponse);
            }
        }
        throw error; // If refresh failed or error is not 401, propagate the error
    }
};

let refreshPromise: Promise<AuthToken> | null = null;

// Refresh the access token using the refresh token
const refreshAccessToken = async (): Promise<AuthToken | null> => {
    if (refreshPromise) {
        // If a refresh is already in progress, wait for it to complete
        return refreshPromise;
    }

    refreshPromise = (async () => {
        const tokens = await getAllIndexedData<AuthToken>(Stores.Auth);
        if (!Array.isArray(tokens)) {
            return null;
        }

        const refreshToken = tokens?.[0]?.refresh_token; // Retrieve stored refresh token
        try {
            const response = await axios.post(
                `${EnvironmentConfig.BaseUrl}/connect/token`,
                {
                    grant_type: 'refresh_token',
                    refresh_token: refreshToken,
                    client_id: EnvironmentConfig.PasswordAuthClientId,
                    client_secret: EnvironmentConfig.PasswordAuthClientSecret,
                },
                {
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                    }
                }
            );

            const newTokens = response.data;
            newTokens.id = tokens?.[0]?.id;
            tokenEventEmitter.emit('tokenRefreshed', newTokens);
            return newTokens;
        } catch (error) {
            console.error('Failed to refresh token:', error);
            tokenEventEmitter.emit('tokenFailed');
            return null;
        } finally {
            refreshPromise = null; // Reset the promise so new refresh attempts can be made
        }
    })();

    return refreshPromise;
};

const processBlobResponse = (response: AxiosResponse) => {

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers["content-disposition"];
    let filename = "thats-me-baby-file";

    if (contentDisposition) {
        // Use regex to extract filename* (UTF-8) if available, otherwise fallback to filename
        const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^;]+)/);
        if (filenameMatch && filenameMatch[1]) {
            filename = decodeURIComponent(filenameMatch[1].replace(/['"]/g, ""));
        }
    }

    return {
        blob: response.data,
        filename
    };
}